import { Link } from 'react-router'
import type { NodeRef } from '../../api'
import { nodePath, type RelayHop, type RelayRole } from './api'
import { ms, pct } from './format'
import { HOP_SPEC } from './states'
import { RoleGlyph, ROLE_HINT, StateText } from './ui'

/*
 * The path of a stream (design/network-console/proto/src/net/path.tsx):
 * ingress → hub (R0: one hop), roles as shapes, each hop with its state and
 * latency. Nodes link to their node card.
 */

/** Role of a node inside a path: first = ingress, last = hub (if it has the role) or egress, middle = relay. */
export function roleAt(i: number, n: number, roles?: readonly string[]): RelayRole {
  if (i === n - 1 && roles?.includes('hub')) return 'hub'
  return i === 0 ? 'ingress' : i === n - 1 ? 'egress' : 'relay'
}

export function NodeName({ node, role, offline, size = 'sm' }: { node: NodeRef; role: RelayRole; offline?: boolean; size?: 'sm' | 'md' }) {
  return (
    <Link
      to={nodePath(node.id)}
      title={`${ROLE_HINT[role]} · ${node.region}`}
      className={`inline-flex items-center gap-1 font-mono hover:underline ${size === 'md' ? 'text-[13px] font-semibold' : 'text-xs'} ${offline ? 'text-fail' : 'text-text'}`}
    >
      <RoleGlyph role={role} size={size === 'md' ? 12 : 10} className="text-text-soft" />
      <span className="sr-only">{role} </span>
      {node.name}
      {offline && <span aria-label="no link">✕</span>}
    </Link>
  )
}

/** Hop as a chip: "● 41 ms" when up, else its state word. */
export function HopChip({ hop }: { hop: RelayHop }) {
  if (hop.state === 'up') {
    return (
      <span className="num rounded-md border border-line-2 px-1.5 font-mono text-[11px] leading-5 whitespace-nowrap text-text-soft" data-testid="hop-chip" data-state="UP" title={`Hop ${hop.from.name} → ${hop.to.name}: latency p50 / p99`}>
        <span className="text-live" aria-hidden>
          ●
        </span>{' '}
        {ms(hop.lat_p50_ms)}
        {hop.lat_p99_ms != null && <span className="text-muted"> / {ms(hop.lat_p99_ms)}</span>}
        {!!hop.retrans_pct && <span className="text-muted"> · {pct(hop.retrans_pct)}</span>}
      </span>
    )
  }
  return <StateText spec={HOP_SPEC[hop.state]} testId="hop-chip" />
}

/** "▷ scw-par-1 —[● 41 ms]— ⬡ vultr-ewr-1": nodes with hops between them. */
export function PathLine({ path, hops, rolesOf, offline, size = 'sm' }: {
  path: NodeRef[]
  hops: RelayHop[]
  rolesOf: (id: string) => readonly string[] | undefined
  offline?: (id: string) => boolean
  size?: 'sm' | 'md'
}) {
  return (
    <ol className="inline-flex flex-wrap items-center gap-x-1.5 gap-y-1" aria-label="Path">
      {path.map((p, i) => {
        const hop = hops.find((h) => h.index === i - 1) ?? hops[i - 1]
        return (
          <li key={`${p.id}-${i}`} className="inline-flex items-center gap-1.5">
            {i > 0 && (
              <>
                <span className="text-faint" aria-hidden>
                  —
                </span>
                {hop ? <HopChip hop={hop} /> : <span className="text-faint">?</span>}
                <span className="text-faint" aria-hidden>
                  →
                </span>
              </>
            )}
            <NodeName node={p} role={roleAt(i, path.length, rolesOf(p.id))} offline={offline?.(p.id)} size={size} />
          </li>
        )
      })}
    </ol>
  )
}
